import { z } from 'zod';
import { TradizioneSacraChiericoSchema, TradizioneSacraPaladinoSchema } from './tradizione';

/**
 * Livelli di incantesimo in DWR (DW classico): 0, 1, 3, 5, 7, 9.
 *
 * - **Livello 0**: cantrip (Trucchetti / Orazioni / Suppliche, etichetta
 *   class-specifica). Non richiedono la mossa *Lanciare Incantesimi*: si
 *   lanciano e basta (manuale draft riga 1178).
 * - **Livelli 1+**: richiedono prese di *Mana* >= livello incantesimo
 *   E livello di classe >= livello incantesimo (manuale draft riga 1180).
 */
export const SpellLevelSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(3),
  z.literal(5),
  z.literal(7),
  z.literal(9),
]);
export type SpellLevel = z.infer<typeof SpellLevelSchema>;

export const SPELL_LEVELS = [0, 1, 3, 5, 7, 9] as const satisfies readonly SpellLevel[];

/**
 * Tag (etichette) di un incantesimo.
 *
 * Allo stato attuale del manuale (DRAFT-V0-20251016) esiste un SOLO tag:
 *  - **continuato**: l'incantesimo, una volta lanciato, mantiene l'effetto
 *    attivo finché qualcosa non lo interrompe. Tipicamente impone -1 a
 *    *lanciare incantesimi* finché attivo (manuale draft riga 1196).
 *
 * Schema enum chiuso per ora: l'introduzione di altri tag richiederà bump.
 */
export const SpellTagSchema = z.enum(['continuato']);
export type SpellTag = z.infer<typeof SpellTagSchema>;

/**
 * Modalità con cui una classe accede a uno specifico incantesimo.
 *
 * Discriminated union per `classId`. Le 5 classi casteggianti DWR
 * (Mago, Chierico, Druido, Warlock, Paladino) sono modellate qui, con
 * eventuali sotto-discriminanti (Tradizione, vincolo di archetipo).
 *
 * - **Mago**: accesso libero al *Corpus Arcano* (nessuna tradizione)
 * - **Warlock**: accesso libero alle mosse del Patrono (nessuna tradizione)
 * - **Druido**: accesso tramite l'unica Tradizione Sacra "della Natura"
 *   (implicita, non si modella esplicitamente)
 * - **Chierico**: accesso vincolato a UNA delle 5 Tradizioni Sacre
 *   (Crepuscolo / Guarigione / Illuminazione / Negromanzia / Valore)
 * - **Paladino**: accesso vincolato a UNA delle 3 Tradizioni Sacre
 *   (Negromanzia / Valore / Natura). La Tradizione **Natura** richiede
 *   l'archetipo Elfo (`requiresArchetype: 'elfo'`); le altre due no.
 *
 * NB: lo Sciamano NON è incluso (mistica diversa, non casteggiante in
 * senso PbtA-DWR).
 */
export const SpellAccessSchema = z.discriminatedUnion('classId', [
  z.object({
    classId: z.literal('mago'),
    /** **FI-24 (D9)**: vedi commento sotto. */
    requiresMove: z.string().min(1).optional(),
  }),
  z.object({
    classId: z.literal('warlock'),
    /** **FI-24 (D9)**: vedi commento sotto. */
    requiresMove: z.string().min(1).optional(),
  }),
  z.object({
    classId: z.literal('druido'),
    /** **FI-24 (D9)**: vedi commento sotto. */
    requiresMove: z.string().min(1).optional(),
  }),
  z.object({
    classId: z.literal('chierico'),
    tradizione: TradizioneSacraChiericoSchema,
    /**
     * Vincolo opzionale: l'archetipo del Chierico richiesto per accedere a
     * questo specifico spell. Si valorizza con `'nano'` per l'orazione
     * *Voce della Terra* (manuale V1, r. 2674: l'archetipo Nano del Chierico
     * aggiunge alla Tradizione Sacra l'orazione *Voce della Terra*).
     * Il valore è l'**ID dell'archetipo** della classe (NON la razza del PG:
     * il giocatore di razza Halfling con archetipo Chierico "Nano" soddisfa
     * questo vincolo).
     */
    requiresArchetype: z.string().min(1).optional(),
    /**
     * **FI-24 (D9)**: vincolo opzionale che condiziona l'accesso allo spell
     * al possesso di una mossa specifica (ID del catalogo mosse, cross-class
     * supportato). Caso d'uso: *Curare Ferite Moderate* del Chierico è
     * disponibile solo se il PG ha la mossa avanzata *Primo Soccorso
     * Migliorato* (manuale V1 r. 6331). Filtro applicato in `isSpellAccessAvailable`.
     */
    requiresMove: z.string().min(1).optional(),
  }),
  z.object({
    classId: z.literal('paladino'),
    tradizione: TradizioneSacraPaladinoSchema,
    /**
     * Vincolo opzionale: l'archetipo della classe richiesto per accedere a
     * questa tradizione. Si valorizza con `'elfo'` per la Tradizione Natura
     * del Paladino. Il valore è l'**ID dell'archetipo** della classe (NON
     * la razza del PG: il giocatore di razza Nano con archetipo Paladino
     * "Elfo" soddisfa questo vincolo).
     */
    requiresArchetype: z.string().min(1).optional(),
    /** **FI-24 (D9)**: vedi commento sopra (chierico). */
    requiresMove: z.string().min(1).optional(),
  }),
]);
export type SpellAccess = z.infer<typeof SpellAccessSchema>;

/**
 * Definizione di un incantesimo nel catalogo `src/data/spells/`.
 *
 * Le istanze sulla scheda PG sono solo riferimenti (`SpellRef` in
 * `character.ts`), non duplicano i dati qui.
 *
 * **Convenzione ID**: `<scope>.<kebab-name>`. Esempi:
 *  - `mago.dardo-incantato`
 *  - `chierico.curare-ferite-leggere`
 *  - `shared.luce` (per spell condivisi tra più classi senza una "casa")
 *
 * **Spell condivisi tra classi**: `availableTo` è un array. Esempio: la
 * Luce (livello 0, continuato) è disponibile sia per il Mago che per il
 * Druido — nessuno duplica il dato, lo stesso oggetto Spell ha 2 entry
 * in `availableTo`.
 */
export const SpellSchema = z.object({
  /** ID stabile globalmente univoco. Es. 'mago.dardo-incantato'. */
  id: z.string().min(1),
  name: z.string().min(1).max(100),
  level: SpellLevelSchema,
  /** Tag dell'incantesimo (oggi solo `continuato` esiste). */
  tags: z.array(SpellTagSchema).default([]),
  /** Descrizione completa dell'effetto (markdown). */
  description: z.string().min(1),
  /**
   * Classi (eventualmente con tradizioni/vincoli) che possono accedere a
   * questo spell. Almeno 1.
   */
  availableTo: z.array(SpellAccessSchema).min(1),
});
export type Spell = z.infer<typeof SpellSchema>;
